import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';

/**
 * Fixed-slot thumbnail reel under the camera: one slot per allowed
 * photo, filled slots show the captured thumbnail, empty slots are
 * placeholders. Tapping a filled thumbnail asks the page to open the
 * photo for inspection; the small red X on its corner asks the page to
 * remove it; tapping an empty slot (when `canAdd`) asks the page to
 * reactivate the camera.
 *
 * The slot elements are exposed for the shared capture animations
 * (photo flying from the camera into its reel slot).
 */
@Component({
  selector: 'cs-scanner-photo-reel',
  templateUrl: 'cs-scanner-photo-reel.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CsScannerPhotoReelComponent {
  /** Captured photos, in capture order. */
  @Input() photos: { dataUrl: string }[] = [];
  /** Total slot count (label scanner: 3, equipment scanner: 4). */
  @Input() set maxSlots(value: number) {
    this.slots = Array.from({ length: value }, (_, i) => i);
  }
  /** True when tapping an empty slot should ask for another photo
   *  (the pages allow this only while the camera is inactive). */
  @Input() canAdd = false;

  /** Tapped the red X on a filled slot — page removes that photo. */
  @Output() remove = new EventEmitter<number>();
  /** Tapped a filled thumbnail — page opens the photo for inspection. */
  @Output() inspect = new EventEmitter<number>();
  /** Tapped an empty slot — page reactivates the camera. */
  @Output() add = new EventEmitter<void>();

  slots: number[] = [];

  @ViewChildren('slot') private slotRefs: QueryList<ElementRef<HTMLElement>>;

  /** Slot elements in slot order, for the shared capture animations. */
  get slotElements(): HTMLElement[] {
    return (this.slotRefs?.toArray() ?? []).map(ref => ref.nativeElement);
  }

  onEmptySlotClick() {
    if (!this.canAdd) return;
    this.add.emit();
  }

  // the X sits on top of the thumbnail — keep its tap from also
  // triggering the thumbnail's inspect
  onRemoveClick(event: Event, index: number) {
    event.stopPropagation();
    this.remove.emit(index);
  }
}
